<template>
  <ViewContainer :title="$t('common.directory')">
    <v-card style="min-height: 600px" class="py-4">
      <v-row class="pb-4 px-4 ma-0 d-flex align-center">
        <v-col
          cols="12"
          md="8"
          lg="8"
          :class="{ 'mb-3': $vuetify.breakpoint.smAndDown }"
        >
          <v-chip
            data-testid="directory-chip-mySite"
            class="mr-4"
            :outlined="selectedFilter !== FILTERS.MY_SITE"
            :color="selectedFilter === FILTERS.MY_SITE ? 'primary' : ''"
            @click="selectFilter(FILTERS.MY_SITE)"
          >
            {{ $t('common.users.people_in_my_site') }}
          </v-chip>
          <v-chip
            data-testid="directory-chip-everywhere"
            class="mr-4"
            :color="selectedFilter === FILTERS.EVERYWHERE ? 'primary' : ''"
            :outlined="selectedFilter !== FILTERS.EVERYWHERE"
            @click="selectFilter(FILTERS.EVERYWHERE)"
          >
            {{ $t('common.users.everywhere') }}
          </v-chip>
        </v-col>

        <v-col cols="12" lg="4" md="4" class="d-flex justify-end">
          <SearchInput
            data-testid="directory_input_search"
            :value="searchTerms"
            @change="handleSearch"
          />
        </v-col>
      </v-row>
      <v-divider class="ma-0" />

      <div v-if="isLoading">
        <v-row
          class="mx-0 mt-4 mb-2 py-0 px-6 d-flex align-center justify-start"
        >
          <v-col cols="3" class="ma-0 pa-0">
            <v-skeleton-loader max-width="100" type="text" />
          </v-col>
          <v-col offset="6" cols="1" class="d-flex justify-end">
            <v-skeleton-loader width="70" type="text" />
          </v-col>
          <v-col cols="1" class="d-flex justify-end">
            <v-skeleton-loader width="70" type="text" />
          </v-col>
          <v-col cols="1" class="d-flex justify-end">
            <v-skeleton-loader width="70" type="text" />
          </v-col>
        </v-row>
        <v-row class="ma-0 pa-0 px-5 d-flex align-center justify-start">
          <v-col
            v-for="item in itemsPerPage"
            class="pa-0"
            cols="12"
            style="margin: 6px;"
            :key="item"
            :style="smallCards ? 'max-width: 138px;' : 'max-width: 168px'"
          >
            <UserCardSkeleton :small="smallCards" />
          </v-col>
        </v-row>
      </div>

      <div v-else>
        <div class="d-flex align-center justify-space-between px-6 mt-4 mb-2">
          <div>
            <p class="ma-0 pa-0 caption blue-grey--text text--darken-2">
              {{ $t('common.resultWithCount', { count: totalResults }) }}
            </p>
          </div>
          <div class="d-flex">
            <v-menu offset-y bottom left :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  text
                  color="white"
                  class="ms-0 px-0 me-5"
                  :disabled="!totalResults"
                  :style="totalResults ? '' : 'opacity : 0.5'"
                >
                  <v-icon color="blue-grey darken-1" left
                    >mdi-account-star-outline</v-icon
                  >
                  <span class="blue-grey--text text--darken-2 caption">
                    {{ $t('common.role') }}
                  </span>
                  <v-icon color="blue-grey darken-1" right
                    >mdi-menu-down</v-icon
                  >
                </v-btn>
              </template>

              <v-list dense nav>
                <v-list-item v-for="option in roleOptions" :key="option.id">
                  <v-checkbox
                    dense
                    class="mt-0 mb-n2"
                    v-model="option.value"
                    :input-value="option.value"
                    @click="onRoleSelected(option.value, option.id)"
                  >
                    <template v-slot:label>
                      <span class="caption blue-grey--text text--darken-3">{{
                        option.text
                      }}</span>
                    </template>
                  </v-checkbox>
                </v-list-item>
              </v-list>
            </v-menu>

            <Menu :closeOnContentClick="false" :options="cardOptions">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  text
                  color="white"
                  class="ms-0 px-0 me-5"
                  :disabled="!totalResults"
                  :style="totalResults ? '' : 'opacity : 0.5'"
                >
                  <v-icon color="blue-grey darken-1" left
                    >mdi-eye-outline</v-icon
                  >
                  <span class="blue-grey--text text--darken-2 caption">
                    {{ $t('common.view') }}
                  </span>
                  <v-icon color="blue-grey darken-1" right
                    >mdi-menu-down</v-icon
                  >
                </v-btn>
              </template>
            </Menu>
            <Menu :closeOnContentClick="false" :options="sortOptions">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  text
                  color="white"
                  class="mx-0 px-0"
                  :disabled="!totalResults"
                  :style="totalResults ? '' : 'opacity : 0.5'"
                >
                  <v-icon color="blue-grey darken-1" left>mdi-sort</v-icon>
                  <span class="blue-grey--text text--darken-2 caption">
                    {{ $t('directory.sort') }}
                  </span>
                  <v-icon color="blue-grey darken-1" right
                    >mdi-menu-down</v-icon
                  >
                </v-btn>
              </template>
            </Menu>
          </div>
        </div>
        <v-row
          v-if="totalResults"
          class="ma-0 pa-0 px-5 d-flex align-center justify-start"
        >
          <v-col
            v-for="result in results"
            class="pa-0"
            cols="12"
            style="margin: 6px;"
            :key="result.account._id"
            :style="smallCards ? 'max-width: 138px;' : 'max-width: 168px'"
          >
            <UserCard
              :small="smallCards"
              :account="result.account"
              :matchingFields="result.matchingFields"
              :highlightTerm="searchTerms || ''"
              @click="handleCardClick(result.account)"
            />
          </v-col>
        </v-row>
        <EmptyState
          v-else
          class="pa-12"
          :icon="'mdi-bee'"
          :iconSize="48"
          :title="$t('common.users.empty_state_subtitle')"
          :message="$t('common.users.search_empty_state_subtitle')"
        />
      </div>
    </v-card>
    <v-row
      v-if="totalPages > 1 && !isLoading"
      class="d-flex justify-center mt-6 pb-4"
    >
      <v-pagination
        circle
        total-visible="5"
        :length="totalPages"
        :value="currentPage"
        @input="handleChangePage"
      ></v-pagination>
    </v-row>

    <UserDetailsDrawer
      :visible="isUserDetailsDrawerVisible"
      :account="selectedAccount"
      @close="handleCloseUserDetailsDrawer"
    />
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import UserCard from './UserCard.vue'
import UserCardSkeleton from './UserCardSkeleton.vue'
import UserDetailsDrawer from './UserDetailsDrawer.vue'
import Menu from '@/components/UI/Menu.vue'
import EmptyState from '@/components/EmptyState.vue'

const DEFAULT_ITEMS_PER_PAGE = 18

const FILTERS = {
  MY_SITE: 'mySite',
  EVERYWHERE: 'everywhere',
}

export default {
  name: 'UsersView',

  mixins: [mixin, trackerMixin],

  components: {
    SearchInput,
    UserCard,
    UserCardSkeleton,
    UserDetailsDrawer,
    ViewContainer,
    EmptyState,
    Menu,
  },

  data() {
    return {
      smallCards: false,
      FILTERS,
      currentPage: this.$route.query.currentPage
        ? Number(this.$route.query.currentPage)
        : 1,
      isLoading: true,
      isUserDetailsDrawerVisible: false,
      results: [],
      searchTerms: this.$route.query.searchTerms ?? '',
      selectedAccount: null,
      totalPages: 0,
      totalResults: 0,
      sortOptions: [
        {
          title: this.$t('common.by_relevance'),
          id: '_score',
          action: () => this.onSortSelected('_score'),
        },
        {
          title: this.$t('common.a_z'),
          id: 'a-z',
          action: () => this.onSortSelected('a-z'),
        },
        {
          title: this.$t('common.z_a'),
          id: 'z-a',
          action: () => this.onSortSelected('z-a'),
        },
      ],
      roleOptions: [
        {
          text: this.$t('common.roles.coach_one'),
          id: 'isCoach',
          value: false,
        },
        {
          text: this.$t('common.roles.impact_validator'),
          id: 'canDoFinancialValidation',
          value: false,
        },
        {
          text: this.$t('common.roles.routine_manager'),
          id: 'isRoutineManager',
          value: false,
        },
      ],
      cardOptions: [
        {
          title: this.$t('common.default'),
          id: 'default',
          value: false,
          action: () => this.onViewSelected({ id: 'default', value: false }),
        },
        {
          title: this.$t('common.small'),
          id: 'small',
          value: true,
          action: () => this.onViewSelected({ id: 'small', value: true }),
        },
      ],
      sort: { by: '_score', order: 'desc' },
      selectedFilter: this.$route.query.selectedFilter ?? FILTERS.MY_SITE,
      filterByRole: {},
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'siteId']),
  },

  created() {
    this.fetchPeople()
  },

  methods: {
    selectFilter(filter) {
      this.selectedFilter = filter
      this.currentPage = 1
      this.trackDirectoryFilter({ filter: filter })
      this.fetchPeople()
    },

    async fetchPeople() {
      try {
        this.isLoading = true
        this.updateQueryParams()
        const response = await api.account.searchPeople(this.organizationId, {
          filters:
            this.selectedFilter === FILTERS.MY_SITE
              ? { ...this.filterByRole, isDeleted: false, site: this.siteId }
              : { ...this.filterByRole, isDeleted: false },
          from: (this.currentPage - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          terms: this.searchTerms,
          sort: this.sort,
          relations: ['organization', 'site', 'photo', 'teams', 'tags'],
          includeMatchingFields: true,
        })
        this.results = response.data?.results ?? []
        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / this.itemsPerPage) || 0
        this.totalResults = response?.data?.totalRecordsInData || 0
        if (this.searchTerms) {
          this.trackDirectorySearch({
            keyword: this.searchTerms,
            isEmpty: !this.totalResults,
          })
        }
      } catch (error) {
        this.teams = []
        this.handleRequestError(error)
      } finally {
        this.isLoading = false
      }
    },

    onSortSelected(option) {
      switch (option) {
        case 'a-z':
          this.sort.by = 'displayname'
          this.sort.order = 'asc'
          break
        case 'z-a':
          this.sort.by = 'displayname'
          this.sort.order = 'desc'
          break
        default:
          this.sort.by = '_score'
          this.sort.order = 'desc'
      }
      this.trackDirectoryNavigate({ element: 'sortSelector', sortBy: option })
      this.fetchPeople()
    },

    onRoleSelected(value, option) {
      if (value) {
        this.filterByRole[option] = value
      } else {
        delete this.filterByRole[option]
      }

      this.trackDirectoryNavigate({
        element: 'roleSelector',
        roles: Object.keys(this.filterByRole),
      })
      this.fetchPeople()
    },

    onViewSelected(option) {
      this.trackDirectoryNavigate({
        element: 'viewSelector',
        view: option.id,
      })
      if (this.smallCards === option.value) return
      this.smallCards = option.value

      if (option.value) {
        this.itemsPerPage = 28
      } else {
        this.itemsPerPage = DEFAULT_ITEMS_PER_PAGE
      }

      this.fetchPeople()
    },

    handleSearch(searchTerms) {
      this.searchTerms = searchTerms
      this.fetchPeople()
    },

    handleChangePage(page) {
      if (page === this.currentPage) return

      this.currentPage = page
      this.trackDirectoryNavigate({ element: 'paginator', page })
      this.fetchPeople()
    },

    handleCardClick(account) {
      this.selectedAccount = account
      this.trackDirectoryNavigate({ element: 'card' })
      this.isUserDetailsDrawerVisible = true
    },

    handleCloseUserDetailsDrawer() {
      this.trackDirectoryNavigate({
        section: 'userDetailDrawer',
        element: 'close',
      })
      this.isUserDetailsDrawerVisible = false
      this.selectedAccount = null
    },

    updateQueryParams() {
      this.$router
        .replace({
          query: {
            currentPage: this.currentPage,
            searchTerms: this.searchTerms,
            selectedFilter: this.selectedFilter,
          },
        })
        .catch(() => {
          // This is intentional to avoid navigation duplicated error
        })
    },
  },
}
</script>
